import React, { createContext, useContext, useMemo, useReducer, useEffect } from 'react';

import { useNewRelicBrowserAgent } from '@notino/react-observability';

import { ReactFCC } from '@types';
import { nonAwaitedPromiseHandler } from '@utils/async';
import { noop } from '@utils/general';

import { REDUCER_INIT_STATE, TUserState, TUserReducer, reducer, IUserActions, useActions } from './reducer';

interface IUserContext {
  state: TUserState;
  actions: TUserContextActions;
}

type TUserContextActions = Pick<IUserActions, 'setUserEmail'>;

const defaultState: IUserContext = {
  state: REDUCER_INIT_STATE,
  actions: {
    setUserEmail: noop,
  },
};

const UserContext = createContext<IUserContext>(defaultState);

export const UserContextProvider: ReactFCC = ({ children }) => {
  const [state, dispatch] = useReducer<TUserReducer>(reducer, REDUCER_INIT_STATE);
  const { loadUser, setUserEmail } = useActions(dispatch);
  const newRelicAgent = useNewRelicBrowserAgent();

  useEffect(() => {
    nonAwaitedPromiseHandler(loadUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state) {
      newRelicAgent?.setUserId(state?.id?.toString() ?? null);
    }
  }, [newRelicAgent, state]);

  const value: IUserContext = useMemo(() => ({ state, actions: { setUserEmail } }), [setUserEmail, state]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => useContext(UserContext);
